import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "../../custom/sub-header/sub-header";
import ReportService from "../../../service/report-service";
import { LINE_URL } from "../../../constant/contact";
import "./my-report-page-style.scss";

//報告收藏庫
//
//  —— 為什麼做成書架而不是清單條 ——
//  使用者買的是「一本命書」。列成一條一條會像訂單紀錄，
//  而訂單紀錄已經有一頁了（P-8）。這裡要的是「我收藏了什麼」，
//  所以用兩欄直式書封 —— 角色圖本來就是 900×1200 的直式。
//
//  資料來自 R-4：它是從訂單撈的，所以訪客買的報告也在裡面
//  （那些報告的 userID 是 guest:<orderID>，照 userID 撈撈不到）。

//角色圖與主色。
//  用 reportType 當 key 而不是 productID：reportType 是報告自己帶著的欄位，
//  也是 config/product-plans 與 engines/ 共用的那一個。
//
//  ⚠️ 圖的路徑跟 constant/products.js 的 image 是同一批檔案。
//  這裡不 import 那份常數是因為它的 reportType 欄位對不上後端
//  （那邊寫的是 ziwei-chart / love-synastry，實際是 jingyue-natal /
//  feihong-natal）—— 直接依賴會在鏡月與緋紅上默默失效。
const FACE = {
  "baiye-natal": { img: "/images/characters/baiye.jpg", tint: "#abfae2", who: "白夜" },
  "bazi-natal": { img: "/images/characters/baiye.jpg", tint: "#abfae2", who: "白夜" },
  "jingyue-natal": { img: "/images/characters/jingyue.jpg", tint: "#8c92ff", who: "鏡月" },
  "feihong-natal": { img: "/images/characters/feihong.jpg", tint: "#d63a52", who: "緋紅" },
};
const FALLBACK_FACE = { img: "", tint: "#d9b26a", who: "命書" };
const faceOf = (t) => FACE[t] || FALLBACK_FACE;

//四種狀態各自能做的事不一樣，所以長相也要不一樣。
//  stalled 是「付了錢但報告根本沒開始」—— 那種單點進閱讀頁只會一直轉，
//  所以直接把人導去客服，不要讓他在轉圈圈的畫面前面等。
const STATE = {
  done: { label: "", cls: "is-done" },
  making: { label: "批算中", cls: "is-making" },
  failed: { label: "需要協助", cls: "is-failed" },
  stalled: { label: "需要協助", cls: "is-failed" },
};

const fmtDate = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

const MyReportPage = () => {
  const navigate = useNavigate();
  const [list, setList] = useState(null); //null = 還在載，[] = 真的空的
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    let alive = true;
    ReportService.library()
      .then((res) => {
        if (!alive) return;
        setList(res.data?.data?.list || []);
      })
      .catch(() => {
        if (!alive) return;
        setError(true);
        setList([]);
      });
    //StrictMode 下 effect 會跑兩次（掛載 → cleanup → 再掛載）。
    //  旗標要在 effect 裡設 true，不能用 useRef(true) 當初始值 ——
    //  第二次掛載不會重新執行 useRef 的初始值，會永遠是 false。
    return () => {
      alive = false;
    };
  }, []);

  //角色籤。只列「他真的有買過的角色」——
  //  列出全部五位的話，沒買過的那幾個永遠是 0，變成一排灰字。
  const tabs = useMemo(() => {
    if (!list?.length) return [];
    const count = new Map();
    for (const b of list) {
      const k = b.reportType;
      count.set(k, (count.get(k) || 0) + 1);
    }
    return [
      { key: "all", name: "全部", n: list.length },
      ...[...count.entries()].map(([k, n]) => ({ key: k, name: faceOf(k).who, n })),
    ];
  }, [list]);

  const shown = useMemo(
    () => (tab === "all" ? list || [] : (list || []).filter((b) => b.reportType === tab)),
    [list, tab],
  );

  const open = (book) => {
    //  failed / stalled 沒有東西可讀，導去閱讀頁只會看到一直轉的畫面。
    if (book.state === "failed" || book.state === "stalled") {
      window.open(LINE_URL, "_blank", "noreferrer");
      return;
    }
    //  making 照樣進得去：那一頁本來就是「等生成 / 看報告」兩用的。
    navigate(`/purchase-result?orderID=${book.orderID}`);
  };

  return (
    <main className="lib">
      <SubHeader title="報告收藏庫" fallback="/account" />

      {list === null && <p className="lib__hint">正在把書搬出來…</p>}

      {list !== null && list.length === 0 && (
        <div className="lib__empty">
          <p className="lib__empty-title">架上還是空的</p>
          <p className="lib__empty-text">
            {error
              ? "暫時取不到你的收藏，稍後再試一次。"
              : "買過的命書會收進這裡，隨時回來都看得到。"}
          </p>
          <button type="button" className="lib__empty-btn" onClick={() => navigate("/")}>
            去看看有哪些
          </button>
        </div>
      )}

      {list !== null && list.length > 0 && (
        <>
          {/* 角色籤。超過螢幕寬就橫向捲，不折行 —— 折行會把書架推下去 */}
          <nav className="lib__tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`lib__tab${tab === t.key ? " is-on" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.name}
                <span className="lib__tab-n">{t.n}</span>
              </button>
            ))}
          </nav>

          <div className="lib__shelf">
            {shown.map((b) => {
              const face = faceOf(b.reportType);
              const st = STATE[b.state] || STATE.making;
              return (
                <button
                  key={b.orderID}
                  type="button"
                  className={`lib__book ${st.cls}`}
                  style={{ "--tint": face.tint }}
                  onClick={() => open(b)}
                >
                  <span className="lib__cover">
                    {face.img && (
                      <img className="lib__face" src={face.img} alt="" loading="lazy" />
                    )}
                    <span className="lib__veil" />

                    {/* 封印。可讀的是完整的一枚，不可讀的是裂開的 */}
                    <span className="lib__seal" aria-hidden="true">
                      {b.state === "done" ? "讀" : b.state === "making" ? "批" : "!"}
                    </span>

                    {st.label && <span className="lib__badge">{st.label}</span>}
                  </span>

                  <span className="lib__title">{b.title}</span>
                  <span className="lib__meta">
                    {b.who && <span className="lib__who">{b.who}</span>}
                    {fmtDate(b.boughtAt)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* 訪客用 A 信箱買、後來用 B 信箱登入就撈不到 ——
              先給一條看得見的出口，不要讓人以為東西不見了 */}
          <p className="lib__rescue">
            買過但這裡沒有？
            <a href={LINE_URL} target="_blank" rel="noreferrer">
              帶著訂單編號找我們
            </a>
          </p>
        </>
      )}
    </main>
  );
};

export default MyReportPage;
